import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AuthContext } from '../App';
import { 
  ArrowLeft, Trophy, Target, Flame, Star, Crown, Medal,
  Zap, Award, Calendar, TrendingUp, Users, CheckCircle2,
  Lock, Sparkles, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gamification = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [challengeAnswers, setChallengeAnswers] = useState({});
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('weekly');
  const [userRank, setUserRank] = useState(null);
  const [newBadges, setNewBadges] = useState([]);

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  useEffect(() => {
    if (user && !loading) {
      loadLeaderboard();
    }
  }, [leaderboardPeriod]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadBadges(),
        loadDailyChallenge(),
        loadLeaderboard()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/gamification/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const loadBadges = async () => {
    try {
      const response = await axios.get(`${API}/gamification/badges`, { withCredentials: true });
      setBadges(response.data.badges);
      
      // Check for new badges
      const checkResponse = await axios.post(`${API}/gamification/check-badges`, {}, { withCredentials: true });
      if (checkResponse.data.new_badges?.length > 0) {
        setNewBadges(checkResponse.data.new_badges);
        toast.success(`🎉 ${checkResponse.data.new_badges.length} yeni rozet kazandın!`);
      }
    } catch (error) {
      console.error('Badges error:', error);
    }
  };

  const loadDailyChallenge = async () => {
    try {
      const response = await axios.get(`${API}/gamification/daily-challenge`, { withCredentials: true });
      setDailyChallenge(response.data);
    } catch (error) {
      console.error('Challenge error:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/gamification/leaderboard?period=${leaderboardPeriod}`, { withCredentials: true });
      setLeaderboard(response.data.leaderboard);
      setUserRank(response.data.user_rank);
    } catch (error) {
      console.error('Leaderboard error:', error);
    }
  };

  const handleChallengeAnswer = (questionIndex, answerIndex) => {
    setChallengeAnswers({
      ...challengeAnswers,
      [questionIndex]: answerIndex
    });
  };

  const submitChallenge = async () => {
    if (!dailyChallenge?.challenge?.exercises) return;
    
    const answers = dailyChallenge.challenge.exercises.map((_, idx) => 
      challengeAnswers[idx] !== undefined ? challengeAnswers[idx] : -1
    );
    
    try {
      const response = await axios.post(
        `${API}/gamification/daily-challenge/submit`,
        { answers },
        { withCredentials: true }
      );
      
      toast.success(`🎉 Challenge tamamlandı! ${response.data.miles_earned} Mil kazandın!`);
      
      if (response.data.new_badges?.length > 0) {
        setNewBadges(response.data.new_badges);
        toast.success(`🏆 ${response.data.new_badges.length} yeni rozet!`);
      }
      
      // Reload data
      await loadAllData();
    } catch (error) {
      toast.error('Challenge gönderilemedi');
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading || !stats) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-8 border-yellow-200 border-t-yellow-500 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-xl font-bold text-gray-700">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
          <Button 
            onClick={() => navigate('/dashboard')} 
            variant="ghost" 
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-bold text-orange-700">{stats?.streak?.current || 0} Gün</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="font-bold text-yellow-700">{stats?.total_xp || 0} Mil</span>
            </div>
          </div>
        </div>

        {/* Title & Level */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full shadow-lg mb-4">
            <span className="text-3xl">{stats?.level_icon}</span>
            <span className="text-xl font-black text-white">{stats?.level}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black">
            <span className="bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
              Oyunlaştırma Merkezi
            </span>
          </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-4 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4 text-center">
              <Trophy className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-yellow-700">{stats?.activities?.exams || 0}</p>
              <p className="text-sm text-gray-600">Sınavlar</p>
            </CardContent>
          </Card>
          <Card className="border-4 border-green-300 bg-green-50">
            <CardContent className="p-4 text-center">
              <Target className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-green-700">{stats?.activities?.exercises || 0}</p>
              <p className="text-sm text-gray-600">Egzersizler</p>
            </CardContent>
          </Card>
          <Card className="border-4 border-blue-300 bg-blue-50">
            <CardContent className="p-4 text-center">
              <Sparkles className="w-10 h-10 text-blue-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-blue-700">{stats?.activities?.lessons || 0}</p>
              <p className="text-sm text-gray-600">Dersler</p>
            </CardContent>
          </Card>
          <Card className="border-4 border-purple-300 bg-purple-50">
            <CardContent className="p-4 text-center">
              <Award className="w-10 h-10 text-purple-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-purple-700">{stats?.badges?.earned || 0}/{stats?.badges?.total || 0}</p>
              <p className="text-sm text-gray-600">Rozetler</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="challenge" className="space-y-6">
          <TabsList className="grid grid-cols-3 max-w-md mx-auto">
            <TabsTrigger value="challenge" className="font-bold">
              <Calendar className="w-4 h-4 mr-2" />
              Günlük
            </TabsTrigger>
            <TabsTrigger value="badges" className="font-bold">
              <Medal className="w-4 h-4 mr-2" />
              Rozetler
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="font-bold">
              <Users className="w-4 h-4 mr-2" />
              Sıralama
            </TabsTrigger>
          </TabsList>

          {/* Daily Challenge Tab */}
          <TabsContent value="challenge">
            <Card className="border-4 border-orange-300">
              <CardHeader className="bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-t-xl">
                <CardTitle className="flex items-center gap-3">
                  <Gift className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-black">Günlük Challenge</h2>
                    <p className="text-sm opacity-90">Her gün yeni sorular, bonus Mil!</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {dailyChallenge?.completed ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-black text-green-600 mb-2">Bugünkü Challenge Tamamlandı!</h3>
                    <p className="text-gray-600 mb-4">Skor: {dailyChallenge.score?.toFixed(0)}%</p>
                    <Badge className="bg-yellow-500 text-white text-lg px-4 py-2">
                      +{dailyChallenge.miles_earned} Mil Kazandın!
                    </Badge>
                    <p className="mt-4 text-sm text-gray-500">Yarın yeni challenge için tekrar gel!</p>
                  </div>
                ) : dailyChallenge?.challenge?.exercises ? (
                  <div className="space-y-6">
                    <div className="bg-orange-50 rounded-lg p-4 mb-4">
                      <h3 className="font-bold text-orange-700">{dailyChallenge.challenge.title}</h3>
                      <p className="text-sm text-gray-600">{dailyChallenge.challenge.description}</p>
                    </div>
                    
                    {dailyChallenge.challenge.exercises.map((exercise, idx) => (
                      <div key={idx} className="bg-white border-2 border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-orange-100 text-orange-700">
                            Soru {idx + 1}
                          </Badge>
                          <Badge variant="outline">{exercise.type}</Badge>
                        </div>
                        <p className="font-semibold text-gray-800 mb-4">{exercise.question}</p>
                        <div className="space-y-2">
                          {exercise.options?.map((option, optIdx) => (
                            <button
                              key={optIdx}
                              onClick={() => handleChallengeAnswer(idx, optIdx)}
                              className={`w-full p-3 rounded-lg text-left transition-all border-2 ${
                                challengeAnswers[idx] === optIdx
                                  ? 'bg-orange-100 border-orange-500 font-bold'
                                  : 'bg-gray-50 border-gray-200 hover:border-orange-300'
                              }`}
                            >
                              <span className="mr-2 text-gray-400">{String.fromCharCode(65 + optIdx)}</span>
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    <Button 
                      onClick={submitChallenge}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg py-6"
                      disabled={Object.keys(challengeAnswers).length < dailyChallenge.challenge.exercises.length}
                    >
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Challenge&apos;ı Tamamla
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Challenge yüklenemedi</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <Card className="border-4 border-purple-300">
              <CardHeader className="bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-t-xl">
                <CardTitle className="flex items-center gap-3">
                  <Medal className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-black">Rozet Koleksiyonu</h2>
                    <p className="text-sm opacity-90">{stats?.badges?.earned || 0} / {stats?.badges?.total || 0} rozet kazanıldı</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {badges.map((badge) => (
                    <div
                      key={badge.id}
                      className={`relative p-4 rounded-xl text-center transition-all ${
                        badge.earned
                          ? 'bg-gradient-to-br from-yellow-100 to-orange-100 border-4 border-yellow-400 shadow-lg'
                          : 'bg-gray-100 border-2 border-gray-200 opacity-60'
                      }`}
                    >
                      {badge.earned && (
                        <div className="absolute -top-2 -right-2">
                          <CheckCircle2 className="w-6 h-6 text-green-500 bg-white rounded-full" />
                        </div>
                      )}
                      <div className={`text-5xl mb-2 ${!badge.earned && 'grayscale'}`}>
                        {badge.earned ? badge.icon : <Lock className="w-12 h-12 mx-auto text-gray-400" />}
                      </div>
                      <h4 className="font-bold text-gray-800">{badge.name}</h4>
                      <p className="text-xs text-gray-600 mt-1">{badge.description}</p>
                      <Badge className={`mt-2 ${badge.earned ? 'bg-yellow-500' : 'bg-gray-400'}`}>
                        +{badge.xp_reward} Mil
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card className="border-4 border-blue-300">
              <CardHeader className="bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-t-xl">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-black">Liderlik Tablosu</h2>
                      <p className="text-sm opacity-90">Sıralamanız: #{userRank || '-'}</p>
                    </div>
                  </div>
                  <select
                    value={leaderboardPeriod}
                    onChange={(e) => setLeaderboardPeriod(e.target.value)}
                    className="bg-white/20 text-white border-2 border-white/30 rounded-lg px-3 py-1 font-bold"
                  >
                    <option value="daily" className="text-gray-800">Bugün</option>
                    <option value="weekly" className="text-gray-800">Bu Hafta</option>
                    <option value="monthly" className="text-gray-800">Bu Ay</option>
                    <option value="all-time" className="text-gray-800">Tüm Zamanlar</option>
                  </select>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {leaderboard.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {leaderboard.map((entry, idx) => (
                      <div
                        key={entry.user_id}
                        className={`flex items-center gap-4 p-4 ${
                          entry.is_current_user ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        } ${idx < 3 ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : ''}`}
                      >
                        <div className="w-12 text-center">
                          <span className={`text-2xl font-black ${
                            idx === 0 ? 'text-yellow-500' :
                            idx === 1 ? 'text-gray-400' :
                            idx === 2 ? 'text-orange-400' : 'text-gray-600'
                          }`}>
                            {getRankIcon(entry.rank)}
                          </span>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                          {entry.picture ? (
                            <img src={entry.picture} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            entry.name?.charAt(0)?.toUpperCase() || '?'
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-800">
                            {entry.name}
                            {entry.is_current_user && <span className="text-blue-500 ml-2">(Sen)</span>}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Flame className="w-4 h-4 text-orange-500" />
                            <span>{entry.streak} gün seri</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-black text-blue-600">{entry.total_xp}</p>
                          <p className="text-xs text-gray-500"> Mil</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Henüz liderlik tablosunda kimse yok</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New Badge Modal */}
        {newBadges.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center animate-bounce-in">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-black text-gray-800 mb-4">Yeni Rozet!</h2>
              <div className="space-y-4 mb-6">
                {newBadges.map((badge) => (
                  <div key={badge.id} className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-4">
                    <div className="text-5xl mb-2">{badge.icon}</div>
                    <h3 className="font-bold text-xl text-gray-800">{badge.name}</h3>
                    <p className="text-sm text-gray-600">{badge.description}</p>
                    <Badge className="bg-yellow-500 text-white mt-2">+{badge.xp_reward} Mil</Badge>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setNewBadges([])}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500"
              >
                Harika! 🎊
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default Gamification;
